import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Icon,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaCalendarCheck,
  FaClock,
  FaChartLine,
  FaUsers,
  FaPhoneAlt,
  FaLink,
  FaCheck,
} from 'react-icons/fa';
import { translate } from 'i18n';
import {
  BookingMethod,
  RoiInputs,
  RoiResults,
  Scenario,
  calculateRoi,
  SCENARIO_MULTIPLIERS,
  AVG_OCCUPANCY,
  DAYS_PER_YEAR,
  WEEKS_PER_YEAR,
  PHONE_AUTOMATION_RATE,
  ADMIN_HOURLY_COST_EUR,
  ADMIN_HOURS_PER_COURT_PER_WEEK,
  COACHING_ONLINE_BOOKING_UPLIFT,
} from '../utils/roiCalculations';
import AnimatedNumber from './AnimatedNumber';
import ResultCard from './ResultCard';
import type { FormulaProps, FormulaFactor } from './FormulaBreakdown';
import MethodologySection, { METHOD_IDS, type MethodId } from './MethodologySection';
import EmailCapture from './EmailCapture';
import { exportRoiPdf } from '../utils/roiPdfExport';

/** Format number for formula display (French locale, no decimals) */
const f = (n: number) => Math.round(n).toLocaleString('fr-FR');
const f1 = (n: number) => n.toLocaleString('fr-FR', { maximumFractionDigits: 1 });

/** Interpolate {placeholders} in an i18n string */
function tip(key: string, vars: Record<string, string | number> = {}): string {
  let text = translate(key as any);
  for (const [k, v] of Object.entries(vars)) {
    text = text.replace(`{${k}}`, typeof v === 'number' ? f(v) : v);
  }
  return text;
}

function buildFormulas(inputs: RoiInputs, results: RoiResults) {
  const scenarioLabel = translate(`marketing.scenario_${inputs.scenario}` as any);

  const noShowFormula: FormulaProps = {
    factors: [
      {
        value: `${f(results.annualNoShowLoss)}€`,
        tooltip: tip('marketing.formula_noshow_loss_tip', {
          bookedRev: f(results.bookedRevenue),
          noShow: inputs.noShowPercent,
        }),
        type: 'derived',
      },
      {
        value: '45%',
        tooltip: translate('marketing.formula_noshow_recovery_tip'),
        type: 'assumption',
      },
    ],
    operator: '×',
    result: `${f(results.recoveredRevenue)}€`,
  };

  const timeFormula: FormulaProps = {
    factors: [
      {
        value: `${inputs.phoneBookingsPerDay}/j`,
        tooltip: translate('marketing.formula_phone_count_tip'),
        type: 'input',
      },
      {
        value: '5 min',
        tooltip: translate('marketing.formula_phone_duration_tip'),
        type: 'assumption',
      },
      {
        value: '7j',
        tooltip: translate('marketing.formula_days_week_tip'),
        type: 'assumption',
      },
    ],
    operator: '×',
    result: `${f1(results.weeklyHoursSaved)}h/sem`,
  };

  const emptySlots = inputs.courts * inputs.hoursPerDay * DAYS_PER_YEAR * (1 - AVG_OCCUPANCY);
  const waitlistFormula: FormulaProps = {
    factors: [
      {
        value: `${f(emptySlots)} créneaux`,
        tooltip: tip('marketing.formula_empty_slots_tip', {
          courts: inputs.courts,
          hours: inputs.hoursPerDay,
          days: DAYS_PER_YEAR,
          empty: Math.round((1 - AVG_OCCUPANCY) * 100),
        }),
        type: 'derived',
      },
      {
        value: '5%',
        tooltip: translate('marketing.formula_waitlist_tip'),
        type: 'assumption',
      },
      {
        value: `${inputs.rate}€/h`,
        tooltip: translate('marketing.formula_your_rate_tip'),
        type: 'input',
      },
    ],
    operator: '×',
    result: `${f(results.waitlistRevenue)}€`,
  };

  const retentionFormula: FormulaProps = {
    factors: [
      {
        value: `${f(results.annualChurnLoss)}€`,
        tooltip: tip('marketing.formula_churn_loss_tip', {
          members: inputs.activeMembers,
          revenue: inputs.monthlyRevenuePerMember,
          churn: inputs.monthlyChurnRate,
        }),
        type: 'derived',
      },
      {
        value: '10%',
        tooltip: translate('marketing.formula_churn_rate_tip'),
        type: 'assumption',
      },
    ],
    operator: '×',
    result: `${f(results.memberRetention)}€`,
  };

  const automationFactors: FormulaFactor[] = [
    {
      value: `${f(results.staffTimeSaved)}€`,
      tooltip: tip('marketing.formula_phone_auto_tip', {
        hours: f1((inputs.phoneBookingsPerDay * 5 * DAYS_PER_YEAR) / 60),
        rate: Math.round(PHONE_AUTOMATION_RATE * 100),
        cost: ADMIN_HOURLY_COST_EUR,
      }),
      type: 'derived',
    },
    {
      value: `${f(results.generalAdminSavings)}€`,
      tooltip: tip('marketing.formula_admin_auto_tip', {
        courts: inputs.courts,
        hpw: f1(ADMIN_HOURS_PER_COURT_PER_WEEK),
        cost: ADMIN_HOURLY_COST_EUR,
        weeks: WEEKS_PER_YEAR,
      }),
      type: 'derived',
    },
  ];
  if (results.coachingUplift > 0) {
    automationFactors.push({
      value: `${f(results.coachingUplift)}€`,
      tooltip: tip('marketing.formula_coaching_tip', {
        coaches: inputs.coaches,
        sessions: inputs.coachingSessionsPerWeek,
        uplift: Math.round(COACHING_ONLINE_BOOKING_UPLIFT * 100),
        rate: inputs.rate,
        weeks: WEEKS_PER_YEAR,
      }),
      type: 'derived',
    });
  }
  const automationFormula: FormulaProps = {
    factors: automationFactors,
    operator: '+',
    result: `${f(results.bookingAutomation)}€`,
  };

  const totalFormula: FormulaProps = {
    factors: [
      {
        value: `${f(results.rawTotal)}€`,
        tooltip: translate('marketing.formula_sum_tip'),
        type: 'derived',
      },
      {
        value: `×${SCENARIO_MULTIPLIERS[inputs.scenario]}`,
        tooltip: tip('marketing.formula_scenario_tip', { scenario: scenarioLabel }),
        type: 'input',
      },
      {
        value: '÷12',
        tooltip: translate('marketing.formula_monthly_tip'),
        type: 'derived',
      },
    ],
    operator: '×',
    result: `${f(results.monthlyTotalImpact)}€/mois`,
  };

  return { noShowFormula, timeFormula, waitlistFormula, retentionFormula, automationFormula, totalFormula };
}

const BOOKING_METHODS: { value: BookingMethod; labelKey: string }[] = [
  { value: 'phone', labelKey: 'marketing.calc_method_phone' },
  { value: 'email', labelKey: 'marketing.calc_method_email' },
  { value: 'whatsapp', labelKey: 'marketing.calc_method_whatsapp' },
  { value: 'other_platform', labelKey: 'marketing.calc_method_other_platform' },
  { value: 'paper', labelKey: 'marketing.calc_method_paper' },
];

const SCENARIOS: { value: Scenario; labelKey: string }[] = [
  { value: 'conservative', labelKey: 'marketing.scenario_conservative' },
  { value: 'moderate', labelKey: 'marketing.scenario_moderate' },
  { value: 'optimistic', labelKey: 'marketing.scenario_optimistic' },
];

const VALID_BOOKING_METHODS: BookingMethod[] = ['phone', 'email', 'whatsapp', 'other_platform', 'paper'];
const VALID_SCENARIOS: Scenario[] = ['conservative', 'moderate', 'optimistic'];

/** Parse URL search params into partial RoiInputs (NaN-safe). */
function parseUrlInputs(): Partial<RoiInputs> {
  const params = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  const partial: Partial<RoiInputs> = {};

  const num = (key: string, min: number, max: number): number | undefined => {
    const raw = params.get(key);
    if (raw == null) return undefined;
    const v = parseFloat(raw);
    return Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : undefined;
  };

  const courts = num('courts', 1, 50);
  if (courts !== undefined) partial.courts = courts;
  const rate = num('rate', 1, 200);
  if (rate !== undefined) partial.rate = rate;
  const hours = num('hoursPerDay', 1, 24);
  if (hours !== undefined) partial.hoursPerDay = hours;
  const noShow = num('noShowPercent', 5, 40);
  if (noShow !== undefined) partial.noShowPercent = noShow;
  const phone = num('phoneBookingsPerDay', 0, 80);
  if (phone !== undefined) partial.phoneBookingsPerDay = phone;
  const members = num('activeMembers', 0, 10000);
  if (members !== undefined) partial.activeMembers = members;
  const revenue = num('monthlyRevenuePerMember', 0, 500);
  if (revenue !== undefined) partial.monthlyRevenuePerMember = revenue;
  const churn = num('monthlyChurnRate', 0, 30);
  if (churn !== undefined) partial.monthlyChurnRate = churn;
  const coaches = num('coaches', 0, 50);
  if (coaches !== undefined) partial.coaches = coaches;
  const sessions = num('coachingSessionsPerWeek', 0, 100);
  if (sessions !== undefined) partial.coachingSessionsPerWeek = sessions;

  const method = params.get('bookingMethod');
  if (method && VALID_BOOKING_METHODS.includes(method as BookingMethod)) {
    partial.bookingMethod = method as BookingMethod;
  }
  const scenario = params.get('scenario');
  if (scenario && VALID_SCENARIOS.includes(scenario as Scenario)) {
    partial.scenario = scenario as Scenario;
  }

  return partial;
}

/** Build a shareable URL from current inputs. */
function buildShareUrl(inputs: RoiInputs): string {
  const base = `${window.location.origin}${window.location.pathname}#/marketing/roi-calculator`;
  const params = new URLSearchParams();
  params.set('courts', String(inputs.courts));
  params.set('rate', String(inputs.rate));
  params.set('hoursPerDay', String(inputs.hoursPerDay));
  params.set('noShowPercent', String(inputs.noShowPercent));
  params.set('bookingMethod', inputs.bookingMethod);
  params.set('phoneBookingsPerDay', String(inputs.phoneBookingsPerDay));
  params.set('activeMembers', String(inputs.activeMembers));
  params.set('monthlyRevenuePerMember', String(inputs.monthlyRevenuePerMember));
  params.set('monthlyChurnRate', String(inputs.monthlyChurnRate));
  params.set('scenario', inputs.scenario);
  if (inputs.coaches > 0) params.set('coaches', String(inputs.coaches));
  if (inputs.coachingSessionsPerWeek > 0)
    params.set('coachingSessionsPerWeek', String(inputs.coachingSessionsPerWeek));
  return `${base}?${params.toString()}`;
}

export default function RoiCalculator() {
  const defaults: RoiInputs = {
    courts: 4,
    rate: 40,
    hoursPerDay: 12,
    noShowPercent: 15,
    bookingMethod: 'phone',
    activeMembers: 200,
    monthlyRevenuePerMember: 45,
    phoneBookingsPerDay: 15,
    monthlyChurnRate: 5,
    coaches: 0,
    coachingSessionsPerWeek: 0,
    scenario: 'conservative',
  };

  const [inputs, setInputs] = useState<RoiInputs>(() => ({
    ...defaults,
    ...parseUrlInputs(),
  }));

  const [showResults, setShowResults] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Auto-calculate if URL had params
  const didAutoCalc = useRef(false);
  useEffect(() => {
    if (didAutoCalc.current) return;
    const urlInputs = parseUrlInputs();
    if (Object.keys(urlInputs).length > 0) {
      didAutoCalc.current = true;
      setShowResults(true);
    }
  }, []);

  const handleCopyLink = useCallback(() => {
    const url = buildShareUrl(inputs);
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  }, [inputs]);
  const { isOpen: isMethodOpen, onOpen: onMethodOpen, onToggle: onMethodToggle } = useDisclosure({ defaultIsOpen: true });
  const [highlightId, setHighlightId] = useState<MethodId | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const results: RoiResults = useMemo(() => calculateRoi(inputs), [inputs]);
  const formulas = useMemo(() => buildFormulas(inputs, results), [inputs, results]);

  const update = useCallback(
    <K extends keyof RoiInputs>(key: K, value: RoiInputs[K]) => {
      setInputs((prev) => ({ ...prev, [key]: value }));
      if (showResults) setShowResults(false);
    },
    [showResults],
  );

  const isMethodOpenRef = useRef(isMethodOpen);
  isMethodOpenRef.current = isMethodOpen;

  const scrollToMethodology = useCallback(
    (id: MethodId) => {
      const wasOpen = isMethodOpenRef.current;
      if (!wasOpen) onMethodOpen();
      setHighlightId(id);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, wasOpen ? 50 : 400);
      setTimeout(() => setHighlightId(null), 3000);
    },
    [onMethodOpen],
  );

  const handleCalculate = useCallback(() => {
    setShowResults(true);
    // Auto-scroll to results after render
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  return (
    <Box maxW="900px" mx="auto" w="100%">
      {/* Inputs */}
      <Box
        bg="gray.50"
        borderRadius="2xl"
        p={{ base: 6, md: 10 }}
        border="1px solid"
        borderColor="gray.100"
      >
        {/* ═══════════ Infrastructure ═══════════ */}
        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
          {/* Courts */}
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
              {translate('marketing.calc_courts')}
            </FormLabel>
            <NumberInput
              value={inputs.courts}
              onChange={(_, v) => update('courts', Math.max(1, v || 1))}
              min={1}
              max={50}
              bg="white"
              borderRadius="xl"
            >
              <NumberInputField borderRadius="xl" />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>

          {/* Hourly rate */}
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
              {translate('marketing.calc_rate')}
            </FormLabel>
            <NumberInput
              value={inputs.rate}
              onChange={(_, v) => update('rate', Math.max(1, v || 1))}
              min={1}
              max={200}
              bg="white"
              borderRadius="xl"
            >
              <NumberInputField borderRadius="xl" />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>

          {/* Hours open */}
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
              {translate('marketing.calc_hours')}
            </FormLabel>
            <NumberInput
              value={inputs.hoursPerDay}
              onChange={(_, v) => update('hoursPerDay', Math.max(1, v || 1))}
              min={1}
              max={24}
              bg="white"
              borderRadius="xl"
            >
              <NumberInputField borderRadius="xl" />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>

          {/* Booking method */}
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
              {translate('marketing.calc_booking_method')}
            </FormLabel>
            <Select
              value={inputs.bookingMethod}
              onChange={(e) => update('bookingMethod', e.target.value as BookingMethod)}
              bg="white"
              borderRadius="xl"
            >
              {BOOKING_METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {translate(m.labelKey as any)}
                </option>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* No-show slider (full width) */}
        <FormControl mt={6}>
          <Flex justify="space-between" align="center" mb={2}>
            <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={0}>
              {translate('marketing.calc_noshow')}
            </FormLabel>
            <Text fontWeight="700" color="brand.500" fontSize="sm">
              {inputs.noShowPercent}%
            </Text>
          </Flex>
          <Slider
            value={inputs.noShowPercent}
            onChange={(v) => update('noShowPercent', v)}
            min={5}
            max={40}
            step={1}
          >
            <SliderTrack bg="gray.200" borderRadius="full" h="6px">
              <SliderFilledTrack bg="brand.500" />
            </SliderTrack>
            <SliderThumb boxSize={5} />
          </Slider>
          <HStack justify="space-between" mt={1}>
            <Text fontSize="xs" color="gray.400">5%</Text>
            <Text fontSize="xs" color="gray.400">{translate('marketing.calc_industry_avg')}</Text>
            <Text fontSize="xs" color="gray.400">40%</Text>
          </HStack>
        </FormControl>

        {/* ═══════════ Members ═══════════ */}
        <Divider my={6} />
        <Text fontWeight="700" fontSize="sm" color="gray.700" mb={4}>
          {translate('marketing.calc_section_members')}
        </Text>

        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
          {/* Active members */}
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
              {translate('marketing.calc_active_members')}
            </FormLabel>
            <NumberInput
              value={inputs.activeMembers}
              onChange={(_, v) => update('activeMembers', Math.max(0, v || 0))}
              min={0}
              max={5000}
              bg="white"
              borderRadius="xl"
            >
              <NumberInputField borderRadius="xl" />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>

          {/* Monthly revenue per member */}
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
              {translate('marketing.calc_monthly_revenue')}
            </FormLabel>
            <NumberInput
              value={inputs.monthlyRevenuePerMember}
              onChange={(_, v) => update('monthlyRevenuePerMember', Math.max(1, v || 1))}
              min={1}
              max={500}
              bg="white"
              borderRadius="xl"
            >
              <NumberInputField borderRadius="xl" />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
        </Grid>

        {/* ═══════════ Booking channel ═══════════ */}
        <Divider my={6} />
        <Text fontWeight="700" fontSize="sm" color="gray.700" mb={4}>
          {translate('marketing.calc_section_bookings')}
        </Text>

        <FormControl>
          <Flex justify="space-between" align="center" mb={2}>
            <Box>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={0}>
                {translate('marketing.calc_phone_bookings')}
              </FormLabel>
              <Text fontSize="xs" color="gray.400">
                {translate('marketing.calc_phone_bookings_help')}
              </Text>
            </Box>
            <Text fontWeight="700" color="brand.500" fontSize="sm">
              {inputs.phoneBookingsPerDay}
            </Text>
          </Flex>
          <Slider
            value={inputs.phoneBookingsPerDay}
            onChange={(v) => update('phoneBookingsPerDay', v)}
            min={0}
            max={80}
            step={1}
          >
            <SliderTrack bg="gray.200" borderRadius="full" h="6px">
              <SliderFilledTrack bg="brand.500" />
            </SliderTrack>
            <SliderThumb boxSize={5} />
          </Slider>
          <HStack justify="space-between" mt={1}>
            <Text fontSize="xs" color="gray.400">0</Text>
            <Text fontSize="xs" color="gray.400">80</Text>
          </HStack>
        </FormControl>

        {/* ═══════════ Advanced options ═══════════ */}
        <Divider my={6} />
        <Accordion allowToggle>
          <AccordionItem border="none">
            <AccordionButton
              px={0}
              _hover={{ bg: 'transparent' }}
            >
              <Text flex={1} textAlign="left" fontSize="sm" fontWeight="700" color="gray.700">
                {translate('marketing.calc_advanced')}
              </Text>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0} pt={4}>
              <VStack spacing={6} align="stretch">
                {/* Monthly churn rate */}
                <FormControl>
                  <Flex justify="space-between" align="center" mb={2}>
                    <Box>
                      <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={0}>
                        {translate('marketing.calc_churn_rate')}
                      </FormLabel>
                      <Text fontSize="xs" color="gray.400">
                        {translate('marketing.calc_churn_rate_help')}
                      </Text>
                    </Box>
                    <Text fontWeight="700" color="brand.500" fontSize="sm">
                      {inputs.monthlyChurnRate}%
                    </Text>
                  </Flex>
                  <Slider
                    value={inputs.monthlyChurnRate}
                    onChange={(v) => update('monthlyChurnRate', v)}
                    min={1}
                    max={15}
                    step={0.5}
                  >
                    <SliderTrack bg="gray.200" borderRadius="full" h="6px">
                      <SliderFilledTrack bg="brand.500" />
                    </SliderTrack>
                    <SliderThumb boxSize={5} />
                  </Slider>
                  <HStack justify="space-between" mt={1}>
                    <Text fontSize="xs" color="gray.400">1%</Text>
                    <Text fontSize="xs" color="gray.400">15%</Text>
                  </HStack>
                </FormControl>

                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
                  {/* Coaches */}
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                      {translate('marketing.calc_coaches')}
                    </FormLabel>
                    <NumberInput
                      value={inputs.coaches}
                      onChange={(_, v) => update('coaches', Math.max(0, v || 0))}
                      min={0}
                      max={20}
                      bg="white"
                      borderRadius="xl"
                    >
                      <NumberInputField borderRadius="xl" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  {/* Coaching sessions per week (only if coaches > 0) */}
                  {inputs.coaches > 0 && (
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                        {translate('marketing.calc_coaching_sessions')}
                      </FormLabel>
                      <NumberInput
                        value={inputs.coachingSessionsPerWeek}
                        onChange={(_, v) => update('coachingSessionsPerWeek', Math.max(0, v || 0))}
                        min={0}
                        max={100}
                        bg="white"
                        borderRadius="xl"
                      >
                        <NumberInputField borderRadius="xl" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                  )}
                </Grid>
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>

        {/* ═══════════ Scenario selector ═══════════ */}
        <Divider my={6} />
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={3}>
            {translate('marketing.calc_scenario')}
          </FormLabel>
          <ButtonGroup size="sm" isAttached w="100%">
            {SCENARIOS.map((s) => (
              <Button
                key={s.value}
                flex={1}
                variant={inputs.scenario === s.value ? 'solid' : 'outline'}
                bg={inputs.scenario === s.value ? 'brand.500' : 'white'}
                color={inputs.scenario === s.value ? 'white' : 'gray.600'}
                borderColor="gray.200"
                fontWeight="600"
                borderRadius="xl"
                _hover={{
                  bg: inputs.scenario === s.value ? 'brand.600' : 'gray.50',
                }}
                onClick={() => update('scenario', s.value)}
              >
                {translate(s.labelKey as any)}
              </Button>
            ))}
          </ButtonGroup>
          <Text fontSize="xs" color="gray.400" mt={2} textAlign="center">
            {translate('marketing.calc_scenario_help')}
          </Text>
        </FormControl>

        {/* CTA */}
        <Button
          mt={8}
          w="100%"
          size="lg"
          bg="brand.500"
          color="white"
          borderRadius="xl"
          fontWeight="700"
          fontSize="md"
          _hover={{ bg: 'brand.600' }}
          onClick={handleCalculate}
        >
          {translate('marketing.calc_cta')}
        </Button>
      </Box>

      {/* Results */}
      {showResults && (
        <VStack spacing={6} mt={10} ref={resultsRef} scrollMarginTop="40px">
          {/* Scenario badge */}
          <Text fontSize="sm" color="gray.500" fontWeight="500">
            {translate('marketing.calc_scenario_label')}{' '}
            <Text as="span" fontWeight="700" color="brand.500">
              {translate(`marketing.scenario_${inputs.scenario}` as any)}
            </Text>
          </Text>

          {/* Row 1: Original 3 cards */}
          <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={5} w="100%">
            <ResultCard
              icon={FaCalendarCheck}
              label={translate('marketing.calc_result_noshow')}
              source={translate('marketing.source_noshow')}
              methodId={METHOD_IDS.noshow}
              onSourceClick={scrollToMethodology}
              formula={formulas.noShowFormula}
            >
              <AnimatedNumber value={results.monthlyNoShow} suffix=" €/mois" />
            </ResultCard>
            <ResultCard
              icon={FaClock}
              label={translate('marketing.calc_result_time')}
              source={translate('marketing.source_time')}
              methodId={METHOD_IDS.time}
              onSourceClick={scrollToMethodology}
              formula={formulas.timeFormula}
            >
              <AnimatedNumber value={results.weeklyHoursSaved} suffix="h/sem" decimals={0} />
            </ResultCard>
            <ResultCard
              icon={FaChartLine}
              label={translate('marketing.calc_result_waitlist')}
              source={translate('marketing.source_waitlist')}
              methodId={METHOD_IDS.waitlist}
              onSourceClick={scrollToMethodology}
              formula={formulas.waitlistFormula}
            >
              <AnimatedNumber value={results.monthlyWaitlist} suffix=" €/mois" />
            </ResultCard>
          </Grid>

          {/* Row 2: Member retention + booking automation */}
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={5} w="100%">
            <ResultCard
              icon={FaUsers}
              label={translate('marketing.calc_result_retention')}
              source={translate('marketing.source_retention')}
              methodId={METHOD_IDS.retention}
              onSourceClick={scrollToMethodology}
              formula={formulas.retentionFormula}
            >
              <AnimatedNumber value={results.monthlyRetention} suffix=" €/mois" />
            </ResultCard>
            <ResultCard
              icon={FaPhoneAlt}
              label={translate('marketing.calc_result_automation')}
              source={translate('marketing.source_automation')}
              methodId={METHOD_IDS.automation}
              onSourceClick={scrollToMethodology}
              formula={formulas.automationFormula}
            >
              <AnimatedNumber value={results.monthlyAutomation} suffix=" €/mois" />
            </ResultCard>
          </Grid>

          {/* Total impact */}
          <ResultCard
            icon={FaChartLine}
            label={translate('marketing.calc_result_total')}
            highlight
            formula={formulas.totalFormula}
          >
            <AnimatedNumber
              value={results.monthlyTotalImpact}
              suffix=" €/mois"
              duration={1.5}
            />
            <Text fontSize="sm" fontWeight="500" color="whiteAlpha.800" mt={1}>
              {f(results.totalAnnualImpact)} €/an · {results.revenueUpliftPercent.toFixed(1)}% du CA
            </Text>
          </ResultCard>

          {/* Trust layer: ROI comparison + Fréjus proof */}
          <Box
            w="100%"
            bg="gray.50"
            border="1px solid"
            borderColor="gray.100"
            borderRadius="2xl"
            p={6}
          >
            <Grid templateColumns={{ base: '1fr', md: '1fr 1px 1fr' }} gap={{ base: 4, md: 6 }} alignItems="center">
              {/* ROI vs subscription */}
              <Box textAlign="center">
                <Text fontSize="xs" fontWeight="600" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                  {translate('marketing.trust_roi_label')}
                </Text>
                <HStack justify="center" spacing={3}>
                  <Box>
                    <Text fontSize="2xl" fontWeight="800" color="brand.500">
                      {f(results.monthlyTotalImpact)} €
                    </Text>
                    <Text fontSize="xs" color="gray.500">{translate('marketing.trust_monthly_gain')}</Text>
                  </Box>
                  <Text fontSize="lg" color="gray.300" fontWeight="700">vs</Text>
                  <Box>
                    <Text fontSize="2xl" fontWeight="800" color="gray.700">
                      99 €
                    </Text>
                    <Text fontSize="xs" color="gray.500">{translate('marketing.trust_subscription')}</Text>
                  </Box>
                </HStack>
                <Text fontSize="sm" fontWeight="700" color="green.500" mt={2}>
                  {translate('marketing.trust_even_half')}
                </Text>
              </Box>

              {/* Vertical divider (desktop only) */}
              <Box display={{ base: 'none', md: 'block' }} bg="gray.200" h="80px" />
              <Divider display={{ base: 'block', md: 'none' }} />

              {/* Fréjus proof badge */}
              <Box textAlign="center">
                <Text fontSize="xs" fontWeight="600" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                  {translate('marketing.trust_proof_label')}
                </Text>
                <HStack justify="center" spacing={4} flexWrap="wrap">
                  <Box>
                    <Text fontSize="xl" fontWeight="800" color="brand.500">-85%</Text>
                    <Text fontSize="xs" color="gray.500">no-shows</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xl" fontWeight="800" color="brand.500">92%</Text>
                    <Text fontSize="xs" color="gray.500">{translate('marketing.trust_prepayment')}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xl" fontWeight="800" color="brand.500">+22%</Text>
                    <Text fontSize="xs" color="gray.500">{translate('marketing.trust_slots')}</Text>
                  </Box>
                </HStack>
                <Text fontSize="xs" color="gray.400" fontStyle="italic" mt={2}>
                  Fréjus Padel Club — 6 {translate('marketing.trust_courts')}
                </Text>
              </Box>
            </Grid>
          </Box>

          {/* Methodology */}
          <MethodologySection
            isOpen={isMethodOpen}
            onToggle={onMethodToggle}
            highlightId={highlightId}
          />

          {/* Email capture */}
          <EmailCapture inputs={inputs} results={results} />

          {/* CTA below results */}
          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={4}
            pt={4}
            w={{ base: '100%', md: 'auto' }}
            align="center"
          >
            <Button
              as="a"
              href="#/auth/sign-up"
              size="lg"
              bg="brand.500"
              color="white"
              borderRadius="full"
              fontWeight="700"
              _hover={{ bg: 'brand.600' }}
              px={8}
              w={{ base: '100%', md: 'auto' }}
            >
              {translate('marketing.start_free_trial')}
            </Button>
            <Button
              size="lg"
              variant="outline"
              borderColor="brand.500"
              color="brand.500"
              borderRadius="full"
              fontWeight="700"
              _hover={{ bg: 'brand.50' }}
              px={8}
              w={{ base: '100%', md: 'auto' }}
              onClick={() => exportRoiPdf(inputs, results)}
            >
              {translate('marketing.pdf_export')}
            </Button>
            <Button
              size="lg"
              variant="ghost"
              color="gray.500"
              borderRadius="full"
              fontWeight="600"
              leftIcon={<Icon as={linkCopied ? FaCheck : FaLink} />}
              _hover={{ bg: 'gray.100', color: 'gray.700' }}
              px={8}
              w={{ base: '100%', md: 'auto' }}
              onClick={handleCopyLink}
            >
              {translate(linkCopied ? 'marketing.link_copied' : 'marketing.copy_link')}
            </Button>
          </Flex>
        </VStack>
      )}
    </Box>
  );
}
